import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import {
  ExperienceRepeater,
  experience_error,
  experience_payload,
  rows_from_saved,
  type ExperienceRow,
} from '@/components/experience-repeater';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import { use_save_walker_details, use_walker_details } from '@/features/walker/hooks';
import { use_languages, use_walker_specializations } from '@/features/lookups/hooks';
import {
  LANGUAGE_OPTIONS,
  WALKER_GENDER_OPTIONS,
  WALKER_SERVICE_RADIUS_OPTIONS,
  WALKER_SPECIALIZATION_OPTIONS,
  type WalkerGender,
  type WalkerServiceRadius,
} from '@/features/walker/types';

type Form = {
  full_name: string;
  date_of_birth: string; // YYYY-MM-DD
  gender: WalkerGender | '';
  service_radius: WalkerServiceRadius;
  years_of_experience: string;
  specialization_codes: string[];
  language_codes: string[];
  experience: ExperienceRow[];
};

const EMPTY: Form = {
  full_name: '',
  date_of_birth: '',
  gender: '',
  service_radius: 'KM_5',
  years_of_experience: '',
  specialization_codes: [],
  language_codes: [],
  experience: [],
};

function years_old(iso_date: string): number {
  const d = new Date(iso_date);
  if (Number.isNaN(d.getTime())) return NaN;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Keys of `Form` whose value is an array — the multi-select pill fields. */
type ArrayKeys<T> = { [K in keyof T]: T[K] extends unknown[] ? K : never }[keyof T];

export function ModuleWalkerPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_walker_details({ enabled: !!draft_q.data });
  const save_m = use_save_walker_details();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});
  // Live lookup tables (admin-extendable); hard-coded seed lists as fallback.
  const specialization_options = use_walker_specializations(WALKER_SPECIALIZATION_OPTIONS);
  const language_options = use_languages(LANGUAGE_OPTIONS);

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('WALKER')) {
      navigate(next_module_step_after('WALKER', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        full_name: saved_q.data.full_name,
        date_of_birth: saved_q.data.date_of_birth.slice(0, 10),
        gender: saved_q.data.gender ?? '',
        service_radius: saved_q.data.service_radius,
        years_of_experience: String(saved_q.data.years_of_experience),
        specialization_codes: saved_q.data.specialization_codes,
        language_codes: saved_q.data.language_codes,
        // Re-entering the form has to show what is stored, or a vendor who came back to
        // fix a typo would silently wipe their history by submitting an empty list.
        experience: rows_from_saved(saved_q.data.experience ?? []),
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  /**
   * Toggle a pill *inside* the updater. Computing `toggle(form.x, v)` at the call
   * site reads the render closure, so several picks landing in one React batch
   * all start from the same stale array and only the last one survives.
   */
  function toggle_in<K extends ArrayKeys<Form>>(key: K, value: Form[K][number]) {
    set_form((f) => ({ ...f, [key]: toggle(f[key] as Form[K][number][], value) }));
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    if (form.full_name.trim().length < 2) {
      set_error('Full name must be at least 2 characters.');
      return;
    }
    if (!form.date_of_birth) {
      set_error('Date of birth is required.');
      return;
    }
    const age = years_old(form.date_of_birth);
    if (!Number.isFinite(age) || age < 18 || age > 100) {
      set_error('Walker must be between 18 and 100 years old.');
      return;
    }

    const yoe = Number(form.years_of_experience);
    if (!Number.isInteger(yoe) || yoe < 0 || yoe > 80) {
      set_error('Years of experience must be a whole number between 0 and 80.');
      return;
    }

    if (form.specialization_codes.length < 1) {
      set_error('Pick at least one specialization.');
      return;
    }
    if (form.language_codes.length < 1) {
      set_error('Pick at least one language.');
      return;
    }

    const experience_problem = experience_error(form.experience);
    if (experience_problem) {
      set_error(experience_problem);
      return;
    }

    try {
      await save_m.mutateAsync({
        full_name: form.full_name.trim(),
        date_of_birth: form.date_of_birth,
        gender: form.gender === '' ? undefined : form.gender,
        service_radius: form.service_radius,
        years_of_experience: yoe,
        specialization_codes: form.specialization_codes,
        language_codes: form.language_codes,
        experience: experience_payload(form.experience),
      });
      if (!draft_q.data) return;
      navigate(next_module_step_after('WALKER', draft_q.data.module_types));
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save walker details', { highlights_fields: true })
          : 'Could not save walker details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-walker"
        title="Pet Walker details"
        description="Tell us who's walking and where. Customers see specializations + languages on your listing."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-walker-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <TextField
          label="Full name"
          name="full_name"
          error={field_errors.full_name}
          data-testid="field-walker-full-name"
          value={form.full_name}
          onChange={(e) => update('full_name', e.target.value)}
          required
          minLength={2}
          maxLength={120}
          placeholder="Rahul Sharma"
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="date_of_birth">Date of birth</Label>
            <DatePicker
              id="date_of_birth"
              data-testid="field-walker-dob"
              value={form.date_of_birth}
              onChange={(v) => update('date_of_birth', v)}
              max={new Date().toISOString().slice(0, 10)}
              placeholder="Select date of birth"
            />
          </div>
          <TextField
            label="Years of experience"
            name="years_of_experience"
            error={field_errors.years_of_experience}
            data-testid="field-walker-yoe"
            value={form.years_of_experience}
            onChange={(e) =>
              update(
                'years_of_experience',
                e.target.value.replace(/\D/g, '').slice(0, 3),
              )
            }
            inputMode="numeric"
            required
            placeholder="3"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="gender">Gender</Label>
            <SelectField
              id="gender"
              data-testid="field-walker-gender"
              value={form.gender}
              onValueChange={(v) => update('gender', v as WalkerGender | '')}
              options={[
                { value: '', label: '— Prefer not to say —' },
                ...WALKER_GENDER_OPTIONS,
              ]}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="service_radius">Service radius</Label>
            <SelectField
              id="service_radius"
              data-testid="field-walker-radius"
              value={form.service_radius}
              onValueChange={(v) => update('service_radius', v as WalkerServiceRadius)}
              options={WALKER_SERVICE_RADIUS_OPTIONS}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Specializations (pick 1–10)</Label>
          <div className="flex flex-wrap gap-2" data-testid="walker-specializations">
            {specialization_options.map((s) => {
              const active = form.specialization_codes.includes(s.code);
              return (
                <button
                  key={s.code}
                  type="button"
                  data-testid={`walker-spec-${s.code}`}
                  data-checked={active}
                  onClick={() => toggle_in('specialization_codes', s.code)}
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {s.display_name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Languages spoken (pick 1–10)</Label>
          <div className="flex flex-wrap gap-2" data-testid="walker-languages">
            {language_options.map((l) => {
              const active = form.language_codes.includes(l.code);
              return (
                <button
                  key={l.code}
                  type="button"
                  data-testid={`walker-lang-${l.code}`}
                  data-checked={active}
                  onClick={() => toggle_in('language_codes', l.code)}
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {l.display_name}
                </button>
              );
            })}
          </div>
        </div>

        <ExperienceRepeater
          noun="their dog"
          testid_prefix="walker"
          rows={form.experience}
          on_change={(rows) => update('experience', rows)}
          disabled={save_m.isPending}
        />
      </StepSection>
    </form>
  );
}
